
import Vue from 'vue';
import EmptyState from '@/components/states/EmptyState.vue';
import LoadingSVG from '@/assets/LoadingSVG.vue';
import TokensSVG from '@/assets/TokensSVG.vue';
import _slice from 'lodash/slice';
import { mapActions, mapGetters, mapState } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { db, functions } from '@/firebase';
import { AppKey, Session, SESSION_STATE } from '@run-diffusion/shared';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import AdminSessionStats from '@/components/AdminStats/AdminSessionStats.vue';
import moment from 'moment-timezone';
import { TeamUserMixin } from '@/mixins';
import { RunnitsOwnerSelection } from '@/store';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';

export default Vue.extend({
	name: 'SessionsHistoryDialog',
	mixins: [
		TeamUserMixin,
	],
	props: {
		value: { type: Boolean, default: false },
	},
	data () {
		return {
			RUNNITS_OWNER_SELECTION,
			currPage: 0,
			pageSize: 50,
			currSessions: [],
			sessions: [],
			lastSessionDoc: null,
			hasMoreSessions: false,
			loadingInitialSessions: false,
			loadingMoreSessions: false,

			sessionsWithNoLogFilesMap: {},
			sessionLogFileUrlDatasToOpen: [],
			loadingLogFileForSessionId: null,
		};
	},
	computed: {
		...mapState([
			'appOffersMap',
			'hardwareOffersMap',
			'softwareOffersMap',
			'user',
			'team',
			'isAdminProxyingUser',
		]),
		...mapGetters([
			'displayLabels',
			'sessionAppKey',
			'sessionSoftwareLabel',
		]),
		canGoPrevPage () {
			return this.currPage > 0;
		},
		canGoNextPage () {
			return (
				(this.lastSessionDoc && this.hasMoreSessions) ||
				this.currPage < Math.floor(this.sessions.length / this.pageSize)
			);
		},
	},
	watch: {
		'runnitState.runnitsOwnerSelection': {
			immediate: true,
			handler (newVal: RunnitsOwnerSelection, oldVal: RunnitsOwnerSelection) {
				if (oldVal && newVal !== oldVal) {
					this.loadInitialSessions(this.value, newVal);
				}
			}
		},
		value: {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal !== oldVal) {
					this.loadInitialSessions(newVal, this.runnitState.runnitsOwnerSelection);
				}
			}
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		onClose () {
			this.$emit('on-close');
		},
		// Returns a display label for the session's app, resolving legacy session.apps[0]
		// or Mobius session.deploymentImage transparently.
		sessionAppLabel (session: Session): string {
			const appKey: AppKey = this.sessionAppKey(session);
			if (appKey) {
				const ao = this.appOffersMap[appKey];
				if (ao) return ao.label;
				return appKey.split('-')[0];
			}
			// No apps array (Mobius) and no resolved deployment image — fall back to raw image ref.
			return session.deploymentImage || '-';
		},
		getInitTimeStr (session: Session) {
			if (!session || !session.initAt || !session.bootAt) return null;
			return `${moment(session.bootAt.toMillis()).diff(session.initAt.toMillis(), 'seconds')} sec`;
		},
		getBootTimeStr (session: Session) {
			if (!session || !session.bootAt || !session.readyAt) return null;
			return `${moment(session.readyAt.toMillis()).diff(session.bootAt.toMillis(), 'seconds')} sec`;
		},
		getReadyMinsDisplay (session: Session) {
			if (session.state !== SESSION_STATE.STOPPED) {
				return 'Currently launched';
			}
			if (!session.readyMillis || session.readyMillis < 1000 * 60) {
				return 'Less than 1 min';
			}
			if (session.readyMillis < 1000 * 60 * 60) {
				return `${Math.floor(session.readyMillis / 1000 / 60)} min ${Math.round(session.readyMillis / 1000 % 60)} sec`;
			}
			return `${Math.floor(session.readyMillis / 1000 / 60 / 60)} hr ${Math.floor(session.readyMillis / 1000 / 60 % 60)} min ${Math.round(session.readyMillis / 1000 % 60)} sec`;
		},
		getTotalTokensUsed (session: Session) {
			return (session.runnitTokensDiff || 0) + (session.freeRunnitTokensDiff || 0);
		},
		onOpenLogFileUrlClick (url: string) {
			if (url) {
				window.open(url, '_blank');
			}
		},
		async onDownloadSessionLogFile (sessionId: string) {
			this.loadingLogFileForSessionId = sessionId;
			const urlDatas: { fileName: string, url: string }[] = await this.getSessionLogFileUrlDatas(sessionId);
			if (urlDatas && urlDatas.length) {
				this.sessionLogFileUrlDatasToOpen = urlDatas;
			} else {
				this.sessionsWithNoLogFilesMap = {
					...this.sessionsWithNoLogFilesMap,
					[sessionId]: true,
				};
				this.updateSnackbar({
					status: SNACKBAR_STATUS.INFO,
					message: 'No logs found for that session',
					show: true,
				});
			}
			this.loadingLogFileForSessionId = null;
		},
		sliceCurrSessions () {
			const startIndex: number = (this.currPage * this.pageSize);
			const endIndex: number = ((this.currPage + 1) * this.pageSize);
			this.currSessions = _slice(
				this.sessions,
				startIndex,
				endIndex,
			);
		},
		onPrevPage () {
			this.currPage = this.currPage - 1;
			if (this.currPage < 0) this.currPage = 0;

			this.sliceCurrSessions();
		},
		async onNextPage () {
			this.currPage = this.currPage + 1;

			if (this.lastSessionDoc && this.hasMoreSessions) {
				const endIndex: number = (this.currPage * this.pageSize);
				if (endIndex >= this.sessions.length) {
					await this.loadMoreSessions();
				} else {
					this.sliceCurrSessions();
				}
			} else {
				this.sliceCurrSessions();
			}
		},
		async getSessionLogFileUrlDatas (sessionId: string) {
			const onError: Function = (e) => {
				console.error('Error loading session logs file: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading session logs file',
					show: true,
				});
			};

			try {
				const functionRef = functions
					.httpsCallable('getSessionLogFileUrlDatas');
				const { urlDatas } = (await functionRef({
					sessionId,
				})).data;
				if (!urlDatas) {
					onError(new Error('getSessionLogFileUrlDatas returned undefined urlDatas'));
				}

				return urlDatas;
			} catch (e) {
				onError(e);
				return [];
			}
		},
		async loadSessions (runnitsOwnerSelection: RunnitsOwnerSelection) {
			try {
				let sessionsRef: any = db.collection('sessions')
					.orderBy('createdAt', 'desc')
					.limit(this.pageSize);
				if (runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER) {
					// index created: sessions: teamId Ascending userId Ascending createdAt Descending __name__ Descending
					sessionsRef = sessionsRef
						.where('userId', '==', this.user.id)
						.where('teamId', '==', null);
				} else if (runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
					// index created: sessions: teamId Ascending createdAt Descending __name__ Descending
					sessionsRef = sessionsRef
						.where('teamId', '==', this.team.id);
				}
				if (this.lastSessionDoc) {
					sessionsRef = sessionsRef
						.startAfter(this.lastSessionDoc);
				}

				const sessionsSnapshot = await sessionsRef.get();
				const result: any[] = sessionsSnapshot.docs.map((doc: any) => {
					return {
						...doc.data(),
						get id () { return doc.id },
					};
				});
				const lastDoc: any = sessionsSnapshot.empty ? null : sessionsSnapshot.docs[sessionsSnapshot.docs.length - 1];

				return {
					success: true,
					result,
					lastDoc,
				};
			} catch (e) {
				console.error('Error loading sessions: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading sessions',
					show: true,
				});

				return {
					success: false,
					result: [],
					lastDoc: null,
				};
			}
		},
		async loadInitialSessions (dialogOpen: boolean, runnitsOwnerSelection: RunnitsOwnerSelection) {
			if (!this.loadingInitialSessions && !this.loadingMoreSessions && dialogOpen) {
				this.loadingInitialSessions = true;
				this.lastSessionDoc = null;
				this.hasMoreSessions = false;
				const { success, result, lastDoc } = await this.loadSessions(runnitsOwnerSelection);
				this.loadingInitialSessions = false;
				if (success) {
					this.currPage = 0;
					this.lastSessionDoc = lastDoc;
					this.hasMoreSessions = result.length === this.pageSize && !!lastDoc;
					this.currSessions = result;
					this.sessions = this.currSessions;
				}
			}
		},
		async loadMoreSessions () {
			if (!this.loadingInitialSessions && !this.loadingMoreSessions) {
				this.loadingMoreSessions = true;
				const { success, result, lastDoc } = await this.loadSessions(this.runnitState.runnitsOwnerSelection);
				this.loadingMoreSessions = false;
				if (success) {
					this.lastSessionDoc = lastDoc;
					this.hasMoreSessions = result.length === this.pageSize && !!lastDoc;
					this.currSessions = result;
					this.sessions = this.sessions.concat(this.currSessions);
					this.$scrollTo('#sessions-history-dialog-scroll-anchor', 200, {
						container: '#sessions-history-dialog-scroll-container',
					});
				}
			}
		},
	},
	components: {
		AdminSessionStats,
		ConfirmDialog,
		LoadingSVG,
		EmptyState,
		TokensSVG,
	},
});
